import { useRef } from 'react';
import type { ChatChannelMemberResponse } from '@/lib/api';

/**
 * Freezes the "unread cutoff" for a channel — the current user's `lastReadAt`
 * (or `joinedAt` if they've never read it) captured the first render the
 * channel's membership is available, BEFORE opening the channel advances
 * `last_read_at` via mark-as-read. The frozen value (epoch ms) anchors the
 * Telegram-style "Unread messages" divider so it stays put while you read
 * instead of vanishing the moment the channel is marked read.
 *
 * Returns null until the matching membership has loaded, when the channel has
 * changed, or when there is no read history to anchor against. Resets when the
 * active channel changes.
 */
export function useUnreadCutoff(
  channelId: string | null,
  member: ChatChannelMemberResponse | null,
): number | null {
  const frozen = useRef<{ channelId: string | null; cutoff: number | null }>({
    channelId: null,
    cutoff: null,
  });

  // Capture once per channel — the first render the matching member is present.
  // Gated on member.channelId so a stale member from the previous channel
  // (during a channel switch) can't seed the wrong cutoff.
  if (
    channelId &&
    member &&
    member.channelId === channelId &&
    frozen.current.channelId !== channelId
  ) {
    const raw = member.lastReadAt ?? member.joinedAt ?? null;
    frozen.current = { channelId, cutoff: raw ? new Date(raw).getTime() : null };
  }

  return frozen.current.channelId === channelId ? frozen.current.cutoff : null;
}
